import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { aggregateData, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const AggregateOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [groupByColumns, setGroupByColumns] = useState([]);
  const [aggregations, setAggregations] = useState([{ column: '', function: 'sum' }]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
        if (result.info.column_names.length > 0) {
          setAggregations([{ column: result.info.column_names[0], function: 'sum' }]);
        }
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const toggleGroupBy = (col) => {
    if (groupByColumns.includes(col)) {
      setGroupByColumns(groupByColumns.filter(c => c !== col));
    } else {
      setGroupByColumns([...groupByColumns, col]);
    }
  };

  const addAggregation = () => {
    setAggregations([...aggregations, { column: columns[0] || '', function: 'sum' }]);
  };

  const removeAggregation = (index) => {
    setAggregations(aggregations.filter((_, i) => i !== index));
  };

  const updateAggregation = (index, field, value) => {
    const updated = [...aggregations];
    updated[index][field] = value;
    setAggregations(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (groupByColumns.length === 0) {
      toast.error(t('aggregate.needGroup'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await aggregateData(groupByColumns, aggregations);
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('aggregate.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label mb-2">{t('aggregate.groupBy')}</label>
        <div className="card p-4 max-h-48 overflow-y-auto space-y-2">
          {columns.map(col => (
            <label key={col} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={groupByColumns.includes(col)}
                onChange={() => toggleGroupBy(col)}
                className="h-4 w-4"
              />
              <span className="text-sm">{col}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="label mb-2">{t('aggregate.list')}</label>
        <div className="space-y-3">
          {aggregations.map((agg, idx) => (
            <div key={idx} className="card p-4">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="label mb-1">{t('form.column')}</label>
                  <select
                    value={agg.column}
                    onChange={(e) => updateAggregation(idx, 'column', e.target.value)}
                    className="select"
                    required
                  >
                    {columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="label mb-1">{t('aggregate.function')}</label>
                  <select
                    value={agg.function}
                    onChange={(e) => updateAggregation(idx, 'function', e.target.value)}
                    className="select"
                    required
                  >
                    <option value="sum">{t('aggregate.sum')}</option>
                    <option value="mean">{t('aggregate.mean')}</option>
                    <option value="count">{t('aggregate.count')}</option>
                    <option value="min">{t('aggregate.min')}</option>
                    <option value="max">{t('aggregate.max')}</option>
                    <option value="std">{t('aggregate.std')}</option>
                  </select>
                </div>
                {aggregations.length > 1 && (
                  <button type="button" onClick={() => removeAggregation(idx)} className="btn-ghost h-8 w-8 p-0 mt-6">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addAggregation} className="btn-secondary flex items-center gap-2 mt-2">
          <Plus className="h-4 w-4" />
          Add Aggregation
        </button>
      </div>

      <MissingChoice reasons={[groupByColumns.length === 0 && t('aggregate.needGroup')]} />

      <div className="flex gap-2 pt-4 border-t">
        <button type="submit" disabled={loading || groupByColumns.length === 0} className="btn-primary flex-1" data-testid="apply-aggregate">
          {loading ? t('form.applying') : t('aggregate.apply')}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default AggregateOperation;